import Modal from '@ui/modal/modal';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import OptionalComponent from '@ui/optional-component/optional-component';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { useProjectUuidParam } from '@/utils/use-project-uuid-param';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { selectPasscodeLists } from '@/store/slices/passcode-lists/selectors';
import CustomSelect from '@ui/select/custom-select/custom-select';
import { useAppSelector } from '@/store/hooks';
import MergeLists from '@/connection/audiences/merge-lists';
import toastTrigger from '@ui/toast/toast-trigger';
import { SelectedMergeAudienceLists } from '@/types/registration.types';
import { getDisplayName } from '../../utils';
import { EToastTypes } from '@ui/toast/toast.types';

type MergeListModalProps = {
  open: boolean;
  setOpen: Dispatch<React.SetStateAction<boolean>>;
  initialSelectedMergeAudienceLists: SelectedMergeAudienceLists;
};

const MergeListModal = ({ open, setOpen, initialSelectedMergeAudienceLists }: MergeListModalProps) => {
  const channel = useAppSelector(getActiveChannel);
  const projectUuidParam = useProjectUuidParam();
  const passcodeLists = useAppSelector(selectPasscodeLists);
  const [saving, setSaving] = useState(false);
  const [selectedMergeAudienceLists, setSelectedMergeAudienceLists] = useState<SelectedMergeAudienceLists>(
    initialSelectedMergeAudienceLists
  );

  useEffect(() => {
    if (open) {
      setSelectedMergeAudienceLists(initialSelectedMergeAudienceLists);
    }
  }, [open, initialSelectedMergeAudienceLists]);

  const handleClose = () => {
    setSelectedMergeAudienceLists(initialSelectedMergeAudienceLists);
    setOpen(false);
    setSaving(false);
  };

  const passcodeListOptions = useMemo(
    () =>
      passcodeLists.map((pl) => ({
        value: pl.passcode_list as number,
        label:
          pl.cloned_source && pl.description
            ? `${getDisplayName(pl.name)} - ${pl.description}`
            : getDisplayName(pl.name),
      })),
    [passcodeLists]
  );

  const handleTargetListChange = useCallback((selected: unknown) => {
    const option = Array.isArray(selected) ? selected[0] : selected;
    if (!option) return;
    setSelectedMergeAudienceLists((prev) => ({
      ...prev,
      targetAudienceList: (option as { value: number }).value,
    }));
  }, []);

  const handleMergeLists = () => {
    if (!selectedMergeAudienceLists.targetAudienceList || !channel || !projectUuidParam) return;

    setSaving(true);

    const abortController = new AbortController();
    MergeLists({
      channel,
      projectUuid: projectUuidParam,
      body: selectedMergeAudienceLists,
      signal: abortController.signal,
    })
      .then((response) => {
        toastTrigger(response.message, {
          type: EToastTypes.info,
          autoClose: 5000,
        });
        handleClose();
      })
      .catch((e) => {
        console.error(e);
        toastTrigger('There was a problem merging the lists', {
          type: EToastTypes.error,
          autoClose: 5000,
        });
        setSaving(false);
      });
  };

  return (
    <Modal
      open={open}
      onRequestClose={handleClose}
      focusLockProps={{ autoFocus: false }}
      customSize={{
        width: '600px',
        minHeight: '300px',
        maxHeight: 'calc(100% - 32px)',
        maxWidth: 'calc(100% - 32px)',
      }}
      contentWrapperClassName="padding-h-40 padding-v-24 overflow-y-auto flex-1 flex-column"
      backgroundColor="var(--gray-900)"
      RenderHeaderContent={() => (
        <Container className="flex-column gap-8" borderRadius="16px">
          <Text className="H4">Add members from list to existing list</Text>
          <Text className="P1" color="var(--gray-500)">
            Select an audience list from your channel. The users in the list will be added to your selection.
          </Text>
        </Container>
      )}
      RenderFooterContent={() => (
        <Container background="var(--gray-900)" className="flex fd-row jc-flex-end ai-flex-end gap-16 margin-t-24">
          <Button
            onClick={handleClose}
            backgroundColor="var(--gray-600)"
            hoverBackgroundColor="var(--gray-500)"
            className="L1 padding-v-16 padding-h-24"
          >
            Cancel
          </Button>
          <Button
            onClick={handleMergeLists}
            className="padding-v-16 padding-h-24"
            disabledOpacity={0.6}
            backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
            disabled={saving}
          >
            <Text
              className="L1"
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            >
              Save
            </Text>
          </Button>
        </Container>
      )}
    >
      <Container className="flex-column gap-8">
        <CustomSelect
          id="merge-target-list-select"
          isMultiSelect={false}
          permanentDropdownIcon
          options={passcodeListOptions}
          onChange={handleTargetListChange}
          Placeholder="Select a target list"
          autoFocus={false}
        />
        <OptionalComponent display={passcodeLists.length === 0}>
          <Container className="flex-column gap-16 ai-center jc-center padding-v-32">
            <Text className="P1" color="var(--gray-500)">
              No lists available. Create a list in your channel to get started.
            </Text>
            <NavLink to="/admin/people">
              <Button
                className="padding-v-16 padding-h-24"
                disabledOpacity={0.6}
                backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
              >
                <Text
                  className="L1"
                  color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                >
                  Manage lists
                </Text>
              </Button>
            </NavLink>
          </Container>
        </OptionalComponent>
      </Container>
    </Modal>
  );
};

export default MergeListModal;
